import { FC, useState, useEffect } from 'react';
import styled from 'styled-components';
import { useDispatch } from 'react-redux';
import { useForm, FormProvider } from 'react-hook-form';
import { ajvResolver } from '@hookform/resolvers/ajv';
import { fullFormats } from 'ajv-formats/dist/formats';
import { useDeepCompareMemo } from '@clatter/platform';
import { config } from '../../../../config';
import { addNotification } from '../../../../store';
import { ProjectPageLayout } from '../../../../layouts';
import { getLocalStorageAccessToken } from '../../../../helpers/auth';
import {
  useGetProjectTemplateByIdQuery,
  useUpdateDocumentMutation,
  useUpdateProjectMutation,
} from '../../../../services';

import { useWorkspace } from '../../../../context/WorkspaceProvider';
import { MicrositesPublishSection } from '../components/MicrositesPublishSection';
import { GenerateDocumentsSection } from '../components/GenerateDocumentsSection';
import { SchemaForm, SchemaTabs, NgCard, NgCardContent } from '../../../UI';

interface ValuesPageProps {
  projectData?: any;
  isLoading: boolean;
  onAction: any;
}

type WorkspaceFormValues = {
  selectedDocumentTemplates: GenerateDocumentTemplateItem[];
};

const StyledGrid = styled.div`
  display: grid;
  grid-template-columns: 2fr 1fr;
  align-items: start;
  grid-gap: ${({ theme }) => theme.spacing.medium};

  .column {
    display: flex;
    flex-direction: column;
    gap: ${({ theme }) => theme.spacing.medium};
  }

  @media (max-width: 1600px) {
    grid-template-columns: 1fr;
  }

`;

const workspaceFormSchema = {
  type: 'object',
  properties: {
    selectedDocumentTemplates: {
      type: 'array',
      minLength: 1,
      errorMessage: {
        minLength: 'Selected document templates can not be empty.',
      },
    },
  },
  required: ['selectedDocumentTemplates'],
};


export const ValuesPage: FC<ValuesPageProps> = ({ projectData, onAction, isLoading: isParentLoading }) => {
  const dispatch = useDispatch();
  const { setLongPullItemId } = useWorkspace();

  const { data: workspaceTemplateData, isLoading: isWorkspaceTemplateDataLoading } = useGetProjectTemplateByIdQuery(
    {
      workspaceId: projectData?.ProjectTemplate?.ID,
    },
    { skip: !projectData?.ProjectTemplate?.ID },
  );
  const [updateDocument, { isLoading: isUpdateDocumentLoading }] = useUpdateDocumentMutation();
  const [updateProject, { isLoading: isUpdateProjectLoading }] = useUpdateProjectMutation();
  const isDataBrowserEnabled = projectData?.Metadata?.features?.enableDataBrowser;

  const isLoading = isParentLoading || isUpdateDocumentLoading || isUpdateProjectLoading;

  const [tab, setTab] = useState<string | null>();
  const tabs: { label: string; value: string }[] = useDeepCompareMemo(() => {
    if (!workspaceTemplateData) return [];

    const template = workspaceTemplateData.DocumentTemplates.find((t) => t.OutputType === 'html')?.Template;
    if (!template) return [];

    // @ts-ignore
    const pages: any[] = template.pages || [];
    if (pages.length === 0) return [];

    const pagesTabs = pages.map((page) => ({
      label: page?.displayName || page?.name || 'Unknown Page',
      value: page.name,
    }));

    if (workspaceTemplateData?.ValuesSchema?.properties) {
      return [{ label: 'Workspace Settings', value: 'default' }, ...pagesTabs];
    }

    return pagesTabs;
  }, [workspaceTemplateData]);

  useEffect(() => {
    if (tabs.length === 0) return;

    setTab(tabs[0].value);
  }, [tabs]);

  const rawSchemas: Record<string, { schema: JSONSchema; initialValues: any; query?: string }> = useDeepCompareMemo(() => {
    const project = projectData?.Values?.find((v: any) => v.name === 'project');
    const html = (workspaceTemplateData?.DocumentTemplates ?? []).find((v: any) => v.OutputType === 'html');

    //@todo this should be taken from document instance not project values
    const htmlData = projectData?.Values?.find((item: any) => item?.name === 'html');

    if (html) {
      const query =
        projectData.Documents.find((d: any) => d.DocumentTemplate?.OutputType === 'html')?.DocumentTemplate?.Query
          ?.Query || '';

      const htmlSchemas = Object.entries(html.ValuesSchema.properties ?? {}).reduce(
        (acc, [page, schema]) => ({
          ...acc,
          [page]: { schema: schema as JSONSchema, initialValues: htmlData.data?.[page] ?? {}, query },
        }),
        { default: { schema: { type: 'object', properties: {}, required: [] } as JSONSchema, initialValues: {} } },
      );

      const projectValues = projectData?.Values?.find((item: any) => item?.name === 'project');
      htmlSchemas['default'] = {
        schema: projectData?.ProjectTemplate?.ValuesSchema ?? { type: 'object', properties: {}, required: [] },
        initialValues: projectValues?.data ?? {},
      };

      return htmlSchemas;
    } else {
      return {
        default: {
          schema: projectData?.ProjectTemplate?.ValuesSchema ?? { type: 'object', properties: {}, required: [] },
          initialValues: project?.data ?? {},
        },
      };
    }
  }, [workspaceTemplateData, projectData]);

  const currentTabData = tab ? rawSchemas[tab] : rawSchemas['default'];

  // workspace state form
  const workspaceFormMethods = useForm<WorkspaceFormValues>({
    mode: 'onChange',
    // @ts-ignore TS2589: Type instantiation is excessively deep and possibly infinite.
    resolver: ajvResolver(workspaceFormSchema || {}, {
      formats: fullFormats,
    }),
    defaultValues: {
      selectedDocumentTemplates: [],
    },
  });

  const onSubmit = async (data: any) => {
    const html = projectData?.Documents?.find((d: any) => d.DocumentTemplate?.OutputType === 'html');

    if (html) {
      const isEditingWorkspaceSettings = tab === 'default';

      if (isEditingWorkspaceSettings) {
        // update workspace
        try {
          await updateProject({
            projectId: projectData.ID,
            data: { values: data },
          }).unwrap();
        } catch (error: any) {
          const msg = 'Workspace update failed. Please try again.';
          dispatch(addNotification({ type: 'error', message: msg }));
          console.error(msg, error);
        }
      } else {
        // update document — nest values under the page key
        const values: Record<string, any> = { ...html.Values };
        if (tab) values[tab] = data;

        const updateResult: any = await updateDocument({
          documentId: html.ID,
          projectId: projectData.ID,
          data: { values },
        });

        if (updateResult?.data?.result !== 'success') {
          const message = 'Couldn\'t save document changes. Please try again.'
          dispatch(addNotification({ type: 'error', message }));
          console.error(message);
          return;
        }
      }

      dispatch(addNotification({ type: 'success', message: `Project updated successfully.` }));
      setLongPullItemId(null);
    } else {
      try {
        await updateProject({
          projectId: projectData.ID,
          data: { values: data },
        }).unwrap();
      } catch (error: any) {
        const msg = 'Workspace update failed. Please try again.';
        dispatch(addNotification({ type: 'error', message: msg }));
        console.error(msg, error);
      }
    }
  };

  const queryGqlData = async (query: string) => {
    const response = await fetch(`${config.assetServiceBaseUrl}/v2/projects/${projectData?.ID}/graphql`, {
      method: 'POST',
      body: JSON.stringify({ query }),
      headers: {
        Authorization: `Bearer ${getLocalStorageAccessToken()}`,
      },
    });

    return await response.json();
  };

  return (
    <ProjectPageLayout noWrapper>
      <StyledGrid>
        <NgCard>
          <NgCardContent>
            <SchemaTabs
              tabs={tabs}
              activeTab={tab ?? ''}
              onTabChange={setTab}
            />

            {currentTabData && (
              <SchemaForm
                key={tab}
                schema={currentTabData.schema}
                initialValues={currentTabData.initialValues}
                isLoading={isLoading}
                query={currentTabData.query}
                fieldKeyPrefix={tab ?? ''}
                queryGqlData={queryGqlData}
                onSubmit={onSubmit}
              />
            )}
          </NgCardContent>
        </NgCard>

        <div className="column">
          <FormProvider {...workspaceFormMethods}>
            <GenerateDocumentsSection
              projectData={projectData}
              workspaceTemplateData={workspaceTemplateData}
              isQueriesEnabled={isDataBrowserEnabled}
              isQueryValid={true}
            />

            <MicrositesPublishSection
              projectData={projectData}
              isQueryValid={true}
              isQueriesEnabled={isDataBrowserEnabled}
              isLoading={isLoading}
            />
          </FormProvider>
        </div>
      </StyledGrid>
    </ProjectPageLayout>
  );
};
